import { CardHeader } from '@material-ui/core';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import CloseIcon from '@material-ui/icons/Close';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { animated, useSpring } from 'react-spring';
import GcpBox from '../gcp-box';
import GcpCurrencyFormat from '../gcp-currency-format';
import GcpTypography from '../gcp-typography';

const useStyles = makeStyles(
  (theme) => ({
    card: {
      border: `1px solid ${theme.palette.common.geyser}`,
      boxSizing: 'border-box',
      boxShadow: '0px 2px 4px rgba(33, 36, 41, 0.05);',
      borderRadius: '16px',
      paddingBottom: theme.spacing(4),
      minWidth: theme.spacing(50),
    },
    header: {
      padding: theme.spacing(2, 2, 0, 2),
    },
    content: {
      position: 'relative',
      padding: theme.spacing(0, 7, 7, 7),
    },
    check: {
      width: theme.spacing(15),
      height: theme.spacing(15),
      color: theme.palette.common.deYork,
    },
    align: {
      textAlign: 'end',
    },
    buttonProgress: {
      color: theme.palette.common.ceriseRed,
      position: 'absolute',
      top: '25%',
      left: '48%',
      marginTop: -12,
      marginLeft: -12,
    },
    [theme.breakpoints.down('sm')]: {
      card: {
        minWidth: 'auto',
      },
    },
  }),
  { name: 'GcpQrCard' }
);

interface GcpQrCardProps {
  amount: string;
  ref1: string;
  ref2: string;
  description: string;
  merchantName: string;
  tidDetail: string;
  qr: string;
  isSuccess: boolean;
  handleClose: () => void;
  currency: string;
  loading: boolean;
}

const GcpQrCard: FC<GcpQrCardProps> = (props) => {
  const {
    amount,
    description,
    merchantName,
    ref1,
    ref2,
    qr,
    isSuccess,
    handleClose,
    currency,
    tidDetail,
    loading,
  } = props;

  const classes = useStyles();
  const { t } = useTranslation();

  const fade = useSpring({
    config: { friction: 10 },
    opacity: !loading ? 1 : 0.025,
  });

  return (
    <Card classes={{ root: classes.card }}>
      <CardHeader
        action={
          <IconButton aria-label="close" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        }
        className={classes.header}
      />
      <CardContent className={classes.content}>
        <GcpTypography align="center" variant="h3">
          {isSuccess ? t('dashboard.success') : t('dashboard.qr_payment')}
        </GcpTypography>
        <GcpBox display="flex" justifyContent="center" mt={3}>
          {isSuccess ? (
            <CheckCircleOutlineIcon className={classes.check} />
          ) : (
            <animated.img
              alt="QR Payment"
              src={`data:image/png;base64, ${qr}`}
              style={fade}
            />
          )}
        </GcpBox>
        {loading && (
          <CircularProgress className={classes.buttonProgress} size={48} />
        )}
        <GcpBox>
          <GcpTypography align="center" paint="hitGray" variant="h3">
            {t('dashboard.amount')}
          </GcpTypography>
        </GcpBox>
        <GcpBox mb={2} mt={1}>
          <GcpCurrencyFormat
            align="center"
            suffix={currency}
            value={amount}
            valueVariant="h2"
          />
        </GcpBox>
        <GcpBox mt={2}>
          <Divider />
        </GcpBox>
        <GcpBox mt={2}>
          <GcpTypography align="center" paint="hitGray" variant="h3">
            {t('dashboard.to')}
          </GcpTypography>
        </GcpBox>
        <GcpBox mt={1}>
          <GcpTypography align="center" variant="h3">
            {merchantName || '-'}
          </GcpTypography>
        </GcpBox>
        <GcpBox mt={1}>
          <GcpTypography align="center" paint="hitGray" variant="body2">
            {tidDetail || '-'}
          </GcpTypography>
        </GcpBox>
        <GcpBox mb={2} mt={2}>
          <Divider />
        </GcpBox>
        <GcpBox mt={2.5}>
          <Grid container spacing={3}>
            <Grid item className={classes.align} xs={4}>
              <GcpTypography paint="hitGray" variant="caption">
                {t('dashboard.ref', { number: 1 })}
              </GcpTypography>
            </Grid>
            <Grid item xs={8}>
              <GcpTypography variant="caption">{ref1 || '-'}</GcpTypography>
            </Grid>
          </Grid>
        </GcpBox>
        <GcpBox mt={2.5}>
          <Grid container spacing={3}>
            <Grid item className={classes.align} xs={4}>
              <GcpTypography paint="hitGray" variant="caption">
                {t('dashboard.ref', { number: 2 })}
              </GcpTypography>
            </Grid>
            <Grid item xs={8}>
              <GcpTypography variant="caption">{ref2 || '-'}</GcpTypography>
            </Grid>
          </Grid>
        </GcpBox>
        <GcpBox mt={2.5}>
          <Grid container spacing={3}>
            <Grid item className={classes.align} xs={4}>
              <GcpTypography paint="hitGray" variant="caption">
                {t('dashboard.desc')}
              </GcpTypography>
            </Grid>
            <Grid item xs={8}>
              <GcpTypography variant="caption">
                {description || '-'}
              </GcpTypography>
            </Grid>
          </Grid>
        </GcpBox>
      </CardContent>
    </Card>
  );
};

export default GcpQrCard;
